import type {
  PortfolioDraft,
  PortfolioDraftPosition,
} from "./portfolioDraft";
import { normalizeAppearanceOverride } from "./appearance";
import type { AppearanceOverride } from "./types";
import {
  projectLocalPortfolioRecordToRenderer,
  LOCAL_PORTFOLIO_STORAGE_SCHEMA_VERSION,
  type LocalPortfolioRecordV1,
  type PortfolioFactoryDependencies,
  type PortfolioValidationIssue,
  type ValidationResult,
  createPortfolioValidationIssue,
  isDecimalString,
  isIsoCalendarDate,
  normalizeCurrencyCode,
  normalizeDecimalString,
  normalizeInstrumentTicker,
  parseDecimalString,
  rfc3339TimestampToEpochMilliseconds,
  toCanonicalUtcTimestamp,
  validateLocalPortfolioRecordV1,
  validationFailure,
} from "./portfolio";
import type {
  DecimalString,
  HoldingV1,
} from "./portfolio";

export interface PortfolioDraftBridgeDependencies
  extends PortfolioFactoryDependencies {
  /**
   * Converts the injected instant to the portfolio's market date. Web passes
   * the Korean market calendar; environment-neutral callers may use UTC.
   */
  readonly marketDate?: (instant: Date) => string;
}

export type PortfolioDraftCompatibilityIssueCode =
  | "name_only_holding"
  | "cross_currency_holding"
  | "purchase_lots_not_supported"
  | "unknown_cost_not_supported"
  | "zero_average_cost_not_supported"
  | "editable_quote_missing"
  | "duplicate_ticker_without_venue_column"
  | "numeric_precision_not_supported";

export interface PortfolioDraftCompatibilityIssue {
  readonly code: PortfolioDraftCompatibilityIssueCode;
  readonly message: string;
  readonly holdingId?: string;
}

export interface PortfolioDraftCompatibility {
  readonly editable: boolean;
  readonly draft?: PortfolioDraft;
  readonly issues: readonly PortfolioDraftCompatibilityIssue[];
}

export interface PortfolioCustomizationDependencies {
  readonly now: () => Date;
}

interface BridgeClock {
  readonly instant: Date;
  readonly timestamp: string;
  readonly marketDate: string;
}

function draftFromContext(
  record: LocalPortfolioRecordV1,
  context: BridgeSourceContext,
): PortfolioDraft | undefined {
  const positions = record.document.holdings.map((holding) =>
    context.draftPositionByHoldingId.get(holding.holdingId),
  );
  if (positions.some((position) => position === undefined)) {
    return undefined;
  }

  return {
    currency: record.document.currency,
    positions: positions as PortfolioDraftPosition[],
  };
}

/**
 * The current four-number table predates the v1 source contract. It is only
 * opened when a round-trip can be proven lossless. Rich/name-only/FX/lot
 * records remain fully viewable and are handed to the decimal-native #8 editor.
 */
export function inspectPortfolioDraftCompatibility(
  record: LocalPortfolioRecordV1 | null,
): PortfolioDraftCompatibility {
  if (!record) {
    return { editable: true, issues: [] };
  }

  const issues: PortfolioDraftCompatibilityIssue[] = [];
  const projection = projectLocalPortfolioRecordToRenderer(record);
  const context = createBridgeSourceContext(record);
  const tickerOwners = new Map<string, string>();

  for (const holding of record.document.holdings) {
    if (!holding.ticker) {
      issues.push({
        code: "name_only_holding",
        holdingId: holding.holdingId,
        message:
          "이름만 등록한 종목은 다음 포트폴리오 편집기에서 안전하게 수정할 수 있어요.",
      });
    } else {
      const ticker = normalizeInstrumentTicker(holding.ticker);
      const previousOwner = tickerOwners.get(ticker);
      if (previousOwner) {
        issues.push({
          code: "duplicate_ticker_without_venue_column",
          holdingId: holding.holdingId,
          message:
            "같은 티커의 거래소를 구분하려면 거래소 열이 있는 다음 편집기가 필요해요.",
        });
      } else {
        tickerOwners.set(ticker, holding.holdingId);
      }
    }

    if (
      holding.currency !== undefined &&
      holding.currency !== record.document.currency
    ) {
      issues.push({
        code: "cross_currency_holding",
        holdingId: holding.holdingId,
        message:
          "외화 종목은 환율과 통화 단위를 보존하는 다음 편집기에서 수정할 수 있어요.",
      });
    }
    if (
      holding.costBasis?.currency !== undefined &&
      holding.costBasis.currency !== record.document.currency
    ) {
      issues.push({
        code: "cross_currency_holding",
        holdingId: holding.holdingId,
        message:
          "외화 원가는 통화 단위를 보존하는 다음 편집기에서 수정할 수 있어요.",
      });
    }

    if (holding.costBasis === undefined) {
      issues.push({
        code: "unknown_cost_not_supported",
        holdingId: holding.holdingId,
        message:
          "원가 미입력 상태를 보존하려면 다음 포트폴리오 편집기가 필요해요.",
      });
    } else if (holding.costBasis.mode === "lots") {
      issues.push({
        code: "purchase_lots_not_supported",
        holdingId: holding.holdingId,
        message:
          "매수 내역별 원가는 현재 간단 편집기에서 손실 없이 수정할 수 없어요.",
      });
    } else if (
      parseDecimalString(holding.costBasis.averageBuyPrice)?.coefficient === 0n
    ) {
      issues.push({
        code: "zero_average_cost_not_supported",
        holdingId: holding.holdingId,
        message:
          "0원 취득 원가를 보존하려면 다음 포트폴리오 편집기가 필요해요.",
      });
    }

    const quote = context.quoteByHoldingId.get(holding.holdingId);
    if (
      !quote ||
      (quote.quoteStatus !== "valid" && quote.quoteStatus !== "stale") ||
      quote.currentPrice === undefined
    ) {
      issues.push({
        code: "editable_quote_missing",
        holdingId: holding.holdingId,
        message:
          "현재가가 없는 종목은 미입력 상태를 구분하는 다음 편집기에서 수정할 수 있어요.",
      });
    }
  }

  for (const warning of projection.warnings) {
    if (
      warning.code === "numeric_projection_clamped" ||
      warning.code === "numeric_projection_rounded"
    ) {
      issues.push({
        code: "numeric_precision_not_supported",
        holdingId: warning.holdingId,
        message:
          "정밀 소수 값을 숫자 입력칸으로 바꾸면 손실될 수 있어 편집을 안전하게 중단했어요.",
      });
    }
  }

  const draft = draftFromContext(record, context);
  if (!draft && !issues.some(({ code }) => code === "numeric_precision_not_supported")) {
    issues.push({
      code: "numeric_precision_not_supported",
      message:
        "이 장부의 정밀 값을 현재 숫자 입력칸으로 안전하게 변환할 수 없어요.",
    });
  }

  return issues.length === 0 && draft
    ? { editable: true, draft, issues: [] }
    : { editable: false, issues };
}

function expandExponential(value: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(value);
  if (!match) {
    return value;
  }

  const sign = match[1] ?? "";
  const integer = match[2] ?? "0";
  const fraction = match[3] ?? "";
  const exponent = Number(match[4]);
  const digits = `${integer}${fraction}`;
  const decimalIndex = integer.length + exponent;

  if (decimalIndex <= 0) {
    return `${sign}0.${"0".repeat(-decimalIndex)}${digits}`;
  }
  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${"0".repeat(decimalIndex - digits.length)}`;
  }
  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
}

function finiteNumberToDecimal(value: number): DecimalString | null {
  if (!Number.isFinite(value)) {
    return null;
  }

  const normalized = Object.is(value, -0) ? 0 : value;
  const expanded = expandExponential(String(normalized));
  if (!isDecimalString(expanded)) {
    return null;
  }

  return normalizeDecimalString(expanded);
}

function readClock(
  dependencies: PortfolioDraftBridgeDependencies,
): ValidationResult<BridgeClock> {
  let instant: Date;
  try {
    instant = dependencies.now();
  } catch {
    return validationFailure([
      createPortfolioValidationIssue(
        "invalid_clock",
        [],
        "현재 시각을 가져오지 못해 기존 장부를 그대로 유지했습니다.",
      ),
    ]);
  }

  const timestamp =
    instant instanceof Date ? toCanonicalUtcTimestamp(instant) : null;
  if (!timestamp) {
    return validationFailure([
      createPortfolioValidationIssue(
        "invalid_clock",
        [],
        "현재 시각이 유효하지 않아 기존 장부를 그대로 유지했습니다.",
      ),
    ]);
  }

  let marketDate: string;
  try {
    marketDate = dependencies.marketDate?.(instant) ?? timestamp.slice(0, 10);
  } catch {
    marketDate = "";
  }

  if (!isIsoCalendarDate(marketDate)) {
    return validationFailure([
      createPortfolioValidationIssue(
        "invalid_date",
        ["manualQuotes", "marketDate"],
        "시장 기준일을 계산하지 못해 기존 장부를 그대로 유지했습니다.",
      ),
    ]);
  }

  return {
    success: true,
    data: { instant, timestamp, marketDate },
    issues: [],
  };
}

function generatedId(
  dependencies: PortfolioDraftBridgeDependencies,
  scope: "portfolio" | "holding",
): string {
  try {
    return dependencies.createId(scope);
  } catch {
    return "";
  }
}

interface BridgeSourceContext {
  readonly holdingById: ReadonlyMap<string, HoldingV1>;
  readonly quoteByHoldingId: ReadonlyMap<
    string,
    NonNullable<LocalPortfolioRecordV1["manualQuotes"]>["quotes"][number]
  >;
  readonly draftPositionByHoldingId: ReadonlyMap<
    string,
    PortfolioDraftPosition
  >;
}

function createBridgeSourceContext(
  record: LocalPortfolioRecordV1 | null,
): BridgeSourceContext {
  if (!record) {
    return {
      holdingById: new Map(),
      quoteByHoldingId: new Map(),
      draftPositionByHoldingId: new Map(),
    };
  }

  const projection = projectLocalPortfolioRecordToRenderer(record);
  const draftPositionByHoldingId = new Map<string, PortfolioDraftPosition>();

  for (const position of projection.portfolio.positions) {
    if (
      typeof position.quantity !== "number" ||
      !Number.isFinite(position.quantity)
    ) {
      continue;
    }

    const quantity = position.quantity;
    const averagePrice =
      position.averagePrice ??
      (position.costBasis !== undefined && quantity > 0
        ? position.costBasis / quantity
        : position.currentPrice ?? 0);
    const currentPrice =
      position.currentPrice ??
      (quantity > 0 ? position.marketValue / quantity : 0);

    if (
      !Number.isFinite(averagePrice) ||
      !Number.isFinite(currentPrice)
    ) {
      continue;
    }

    const holdingId = position.instrumentId.startsWith("holding:")
      ? position.instrumentId.slice("holding:".length)
      : position.instrumentId;
    draftPositionByHoldingId.set(holdingId, {
      id: holdingId,
      ticker: position.ticker,
      quantity,
      averagePrice,
      currentPrice,
    });
  }

  return {
    holdingById: new Map(
      record.document.holdings.map((holding) => [
        holding.holdingId,
        holding,
      ]),
    ),
    quoteByHoldingId: new Map(
      (record.manualQuotes?.quotes ?? []).map((quote) => [
        quote.holdingId,
        quote,
      ]),
    ),
    draftPositionByHoldingId,
  };
}

function matchingHolding(
  context: BridgeSourceContext,
  draftId: string,
): HoldingV1 | undefined {
  const rawId = draftId.startsWith("holding:")
    ? draftId.slice("holding:".length)
    : draftId;
  return context.holdingById.get(rawId);
}

function changed(
  draft: PortfolioDraftPosition,
  source: PortfolioDraftPosition | undefined,
  field: "quantity" | "averagePrice" | "currentPrice",
): boolean {
  return !source || draft[field] !== source[field];
}

function decimalIssue(
  index: number,
  field: "quantity" | "averagePrice" | "currentPrice",
): PortfolioValidationIssue {
  const labels = {
    quantity: "수량",
    averagePrice: "평균 단가",
    currentPrice: "현재가",
  } as const;
  const particles = {
    quantity: "을",
    averagePrice: "를",
    currentPrice: "를",
  } as const;

  return createPortfolioValidationIssue(
    "invalid_decimal",
    ["positions", index, field],
    `${labels[field]}${particles[field]} 안전한 소수 형식으로 저장할 수 없습니다.`,
  );
}

function buildHolding(
  position: PortfolioDraftPosition,
  index: number,
  sourceContext: BridgeSourceContext,
  refreshQuoteSet: boolean,
  timestamp: string,
  dependencies: PortfolioDraftBridgeDependencies,
): ValidationResult<{
  readonly holding: Readonly<Record<string, unknown>>;
  readonly quote: Readonly<Record<string, unknown>>;
}> {
  const existing = matchingHolding(sourceContext, position.id);
  const source = existing
    ? sourceContext.draftPositionByHoldingId.get(existing.holdingId)
    : undefined;
  const quantityChanged = changed(position, source, "quantity");
  const averagePriceChanged = changed(position, source, "averagePrice");
  const currentPriceChanged = changed(position, source, "currentPrice");
  const preserveLots =
    existing?.costBasis?.mode === "lots" &&
    !quantityChanged &&
    !averagePriceChanged;
  const preserveUnknownCost =
    existing !== undefined &&
    existing.costBasis === undefined &&
    !quantityChanged &&
    !averagePriceChanged;
  const quantity = quantityChanged
    ? finiteNumberToDecimal(position.quantity)
    : existing?.quantity ?? null;
  const averagePrice = preserveLots
    ? null
    : averagePriceChanged
      ? finiteNumberToDecimal(position.averagePrice)
      : existing?.costBasis?.mode === "average"
        ? existing.costBasis.averageBuyPrice
        : finiteNumberToDecimal(position.averagePrice);
  const currentPrice = currentPriceChanged
    ? finiteNumberToDecimal(position.currentPrice)
    : finiteNumberToDecimal(position.currentPrice);
  const issues: PortfolioValidationIssue[] = [];

  if (!quantity) {
    issues.push(decimalIssue(index, "quantity"));
  }
  if (!preserveLots && !preserveUnknownCost && !averagePrice) {
    issues.push(decimalIssue(index, "averagePrice"));
  }
  if (!currentPrice) {
    issues.push(decimalIssue(index, "currentPrice"));
  }
  if (
    issues.length > 0 ||
    !quantity ||
    (!preserveLots && !preserveUnknownCost && !averagePrice) ||
    !currentPrice
  ) {
    return validationFailure(issues);
  }

  const sourceQuote = existing
    ? sourceContext.quoteByHoldingId.get(existing.holdingId)
    : undefined;
  const quoteObservationUnchanged =
    !refreshQuoteSet &&
    existing !== undefined &&
    source !== undefined &&
    !currentPriceChanged;
  const costBasis: Readonly<Record<string, unknown>> | undefined =
    preserveUnknownCost
      ? undefined
      : preserveLots
        ? existing.costBasis
        : {
            mode: "average",
            ...(existing?.costBasis?.currency
              ? { currency: existing.costBasis.currency }
              : {}),
            averageBuyPrice: averagePrice!,
          };
  const ticker = normalizeInstrumentTicker(position.ticker);
  const previousName = existing?.name;
  const name =
    previousName && previousName !== existing?.ticker
      ? previousName
      : ticker;
  const holdingId =
    existing?.holdingId ?? generatedId(dependencies, "holding");

  const holding: Readonly<Record<string, unknown>> = {
    holdingId,
    assetKind: existing?.assetKind ?? "stock",
    ticker,
    name,
    ...(existing?.market ? { market: existing.market } : {}),
    ...(existing?.exchange ? { exchange: existing.exchange } : {}),
    ...(existing?.currency ? { currency: existing.currency } : {}),
    quantity,
    ...(costBasis ? { costBasis } : {}),
    ...(existing?.firstPurchasedAt
      ? { firstPurchasedAt: existing.firstPurchasedAt }
      : {}),
    ...(existing?.memo !== undefined ? { memo: existing.memo } : {}),
    ...(existing?.customization
      ? { customization: existing.customization }
      : {}),
    createdAt: existing?.createdAt ?? timestamp,
    updatedAt: timestamp,
  };

  const quote: Readonly<Record<string, unknown>> = quoteObservationUnchanged
    ? sourceQuote
      ? quantityChanged
        ? (() => {
            // An explicit evaluated amount and the absolute daily change both
            // depend on the old quantity. Preserve the observed price,
            // status, and percentage signal, then let the adapter recompute
            // market value from the new quantity.
            const quantityIndependentQuote = Object.fromEntries(
              Object.entries(sourceQuote).filter(
                ([field]) =>
                  field !== "marketValue" &&
                  field !== "dailyChangeAmount",
              ),
            );
            return {
              ...quantityIndependentQuote,
              holdingId,
            };
          })()
        : {
            ...sourceQuote,
            holdingId,
          }
      : {
          holdingId,
          quoteStatus: "missing",
        }
    : {
        holdingId,
        quoteStatus: "valid",
        currentPrice,
      };

  return {
    success: true,
    data: { holding, quote },
    issues: [],
  };
}

function requiresQuoteSetRefresh(
  draft: PortfolioDraft,
  previous: LocalPortfolioRecordV1 | null,
  sourceContext: BridgeSourceContext,
): boolean {
  if (
    !previous ||
    normalizeCurrencyCode(draft.currency) !== previous.document.currency ||
    draft.positions.length !== previous.document.holdings.length
  ) {
    return true;
  }

  return draft.positions.some((position) => {
    const existing = matchingHolding(sourceContext, position.id);
    const source = existing
      ? sourceContext.draftPositionByHoldingId.get(existing.holdingId)
      : undefined;
    if (!existing || !source || !existing.ticker) {
      return true;
    }

    return (
      normalizeInstrumentTicker(position.ticker) !==
        normalizeInstrumentTicker(existing.ticker) ||
      changed(position, source, "currentPrice")
    );
  });
}

/**
 * Converts the existing numeric demo editor result into the exact v1 storage
 * contract. Any failed conversion aborts the entire operation; callers must
 * keep the previous record and surface the returned Korean issue.
 */
export function createPortfolioRecordFromDraft(
  draft: PortfolioDraft,
  previous: LocalPortfolioRecordV1 | null,
  dependencies: PortfolioDraftBridgeDependencies,
): ValidationResult<LocalPortfolioRecordV1> {
  const compatibility = inspectPortfolioDraftCompatibility(previous);
  if (!compatibility.editable) {
    return validationFailure(
      compatibility.issues.map((issue) => {
        const holdingIndex = issue.holdingId
          ? previous?.document.holdings.findIndex(
              ({ holdingId }) => holdingId === issue.holdingId,
            )
          : -1;
        return createPortfolioValidationIssue(
          "invalid_value",
          holdingIndex !== undefined && holdingIndex >= 0
            ? ["holdings", holdingIndex]
            : ["holdings"],
          issue.message,
        );
      }),
    );
  }

  const clock = readClock(dependencies);
  if (!clock.success) {
    return clock;
  }

  const sourceContext = createBridgeSourceContext(previous);
  const refreshQuoteSet = requiresQuoteSetRefresh(
    draft,
    previous,
    sourceContext,
  );
  const built = draft.positions.map((position, index) =>
    buildHolding(
      position,
      index,
      sourceContext,
      refreshQuoteSet,
      clock.data.timestamp,
      dependencies,
    ),
  );
  const issues = built.flatMap((result) =>
    result.success ? [] : result.issues,
  );
  if (issues.length > 0) {
    return validationFailure(issues);
  }

  const values = built.flatMap((result) =>
    result.success ? [result.data] : [],
  );
  const previousDocument = previous?.document;
  const document = {
    schemaVersion: 1,
    portfolioId:
      previousDocument?.portfolioId ?? generatedId(dependencies, "portfolio"),
    currency: normalizeCurrencyCode(draft.currency),
    ...(previousDocument?.farmUnit
      ? { farmUnit: previousDocument.farmUnit }
      : {}),
    holdings: values.map(({ holding }) => holding),
    createdAt: previousDocument?.createdAt ?? clock.data.timestamp,
    updatedAt: clock.data.timestamp,
  };
  const manualQuotes = {
    schemaVersion: 1,
    portfolioId: document.portfolioId,
    currency: document.currency,
    asOf:
      !refreshQuoteSet && previous?.manualQuotes
        ? previous.manualQuotes.asOf
        : clock.data.timestamp,
    marketDate:
      !refreshQuoteSet && previous?.manualQuotes
        ? previous.manualQuotes.marketDate
        : clock.data.marketDate,
    quotes: values.map(({ quote }) => quote),
  };
  const initial = validateLocalPortfolioRecordV1({
    storageSchemaVersion: LOCAL_PORTFOLIO_STORAGE_SCHEMA_VERSION,
    document,
    manualQuotes,
  });
  if (!initial.success) {
    return initial;
  }

  const projection = projectLocalPortfolioRecordToRenderer(initial.data);
  if (!projection.needsFarmUnitPersistence) {
    return initial;
  }

  return validateLocalPortfolioRecordV1({
    ...initial.data,
    document: {
      ...initial.data.document,
      farmUnit: projection.farmUnitPersistenceValue,
    },
  });
}

/**
 * Persists manual-farm appearance in the authoritative holding document so a
 * future Worker, shared room, or Mac shell receives the same pet.
 */
export function updatePortfolioHoldingCustomization(
  record: LocalPortfolioRecordV1,
  instrumentId: string,
  value: AppearanceOverride | null,
  dependencies: PortfolioCustomizationDependencies,
): ValidationResult<LocalPortfolioRecordV1> {
  const holdingId = instrumentId.startsWith("holding:")
    ? instrumentId.slice("holding:".length)
    : instrumentId;
  const holdingIndex = record.document.holdings.findIndex(
    (holding) => holding.holdingId === holdingId,
  );
  if (holdingIndex < 0) {
    return validationFailure([
      createPortfolioValidationIssue(
        "holding_not_found",
        ["holdingId"],
        "꾸밀 보유 종목을 찾을 수 없습니다.",
      ),
    ]);
  }

  let timestamp: string | null = null;
  try {
    const now = dependencies.now();
    timestamp =
      now instanceof Date ? toCanonicalUtcTimestamp(now) : null;
  } catch {
    // Returned below as an environment-neutral clock issue.
  }
  if (!timestamp) {
    return validationFailure([
      createPortfolioValidationIssue(
        "invalid_clock",
        [],
        "현재 시각을 가져오지 못해 꾸미기를 저장하지 않았습니다.",
      ),
    ]);
  }

  const holding = record.document.holdings[holdingIndex];
  if (!holding) {
    return validationFailure([
      createPortfolioValidationIssue(
        "holding_not_found",
        ["holdingId"],
        "꾸밀 보유 종목을 찾을 수 없습니다.",
      ),
    ]);
  }
  const nextTime = rfc3339TimestampToEpochMilliseconds(timestamp);
  const documentTime = rfc3339TimestampToEpochMilliseconds(
    record.document.updatedAt,
  );
  const holdingTime = rfc3339TimestampToEpochMilliseconds(holding.updatedAt);
  if (
    nextTime === null ||
    documentTime === null ||
    holdingTime === null ||
    nextTime < documentTime ||
    nextTime < holdingTime
  ) {
    return validationFailure([
      createPortfolioValidationIssue(
        "chronology",
        ["updatedAt"],
        "꾸미기 저장 시각은 기존 수정 시각보다 빠를 수 없습니다.",
      ),
    ]);
  }

  const customization =
    value === null
      ? {
          ...(holding.customization?.basePaletteId
            ? { basePaletteId: holding.customization.basePaletteId }
            : {}),
          ...(holding.customization?.baseHatId
            ? { baseHatId: holding.customization.baseHatId }
            : {}),
        }
      : {
          ...holding.customization,
          ...normalizeAppearanceOverride(value),
        };
  const holdings = [...record.document.holdings];
  const holdingWithoutCustomization = { ...holding };
  delete holdingWithoutCustomization.customization;
  holdings[holdingIndex] = {
    ...holdingWithoutCustomization,
    ...(Object.keys(customization).length > 0
      ? { customization }
      : {}),
    updatedAt: timestamp,
  };

  return validateLocalPortfolioRecordV1({
    ...record,
    document: {
      ...record.document,
      holdings,
      updatedAt: timestamp,
    },
  });
}

/**
 * Presents a v1 record to the current table editor while retaining durable
 * holding IDs in its row keys. The #8 editor will consume decimal strings
 * directly; this compatibility bridge never mutates the source record.
 */
export function portfolioRecordToDraft(
  record: LocalPortfolioRecordV1 | null,
): PortfolioDraft | undefined {
  return inspectPortfolioDraftCompatibility(record).draft;
}
